import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, Loader2, AlertCircle } from 'lucide-react';
import { LaborCalendarModalShell } from '../../calendario/components/LaborCalendarModalShell';
import { toDateInputValue } from '../../calendario/utils/laborCalendarUtils';
import { getEmployeeDisplayName } from '../../calendario/utils/laborFactoryUtils';
import { employeeService } from '../../empleados/services/employeeService';
import { laborAbsenceService } from '../services/laborAbsenceService';
import { ABSENCE_TYPE_OPTIONS } from '../utils/laborAbsenceUtils';
import { useFactory } from '../../../../context/FactoryContext';

export function LaborAbsenceFormModal({
  isOpen,
  onClose,
  factoryId,
  editingAbsence = null,
  defaultEmployeeId = null,
  employeeOptions = null,
  onSuccess,
}) {
  const queryClient = useQueryClient();
  const { selectedFactory } = useFactory();
  const factoryBase = selectedFactory || '';
  const isEdit = Boolean(editingAbsence?.id);

  const [employeeId, setEmployeeId] = useState('');
  const [absenceType, setAbsenceType] = useState('vacation');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  const { data: employees = [], isLoading: loadingEmployees } = useQuery({
    queryKey: ['labor-absence-employees', factoryId, factoryBase],
    queryFn: () => employeeService.getEmployees(factoryBase, true, factoryId),
    enabled: isOpen && employeeOptions == null && Boolean(factoryId || factoryBase),
  });

  const selectableEmployees = employeeOptions ?? employees;

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    if (editingAbsence) {
      setEmployeeId(String(editingAbsence.employee_id ?? ''));
      setAbsenceType(editingAbsence.absence_type || 'vacation');
      setStartDate(toDateInputValue(editingAbsence.start_date));
      setEndDate(toDateInputValue(editingAbsence.end_date));
      setReason(editingAbsence.reason || '');
    } else {
      setEmployeeId(defaultEmployeeId != null && defaultEmployeeId !== '' ? String(defaultEmployeeId) : '');
      setAbsenceType('vacation');
      setStartDate('');
      setEndDate('');
      setReason('');
    }
  }, [isOpen, editingAbsence, defaultEmployeeId]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!employeeId) throw new Error('Selecciona un empleado');
      if (!startDate || !endDate) throw new Error('Indica fecha de inicio y fin');
      if (startDate > endDate) throw new Error('La fecha de inicio no puede ser posterior a la de fin');

      if (isEdit) {
        return laborAbsenceService.updateAbsence(editingAbsence.id, {
          absenceType,
          startDate,
          endDate,
          reason,
        });
      }
      return laborAbsenceService.createAbsence({
        employeeId,
        absenceType,
        startDate,
        endDate,
        reason,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['labor-absences'] });
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      setError(err?.message || 'No se pudo guardar la ausencia');
    },
  });

  const footer = (
    <>
      <button
        type="button"
        onClick={onClose}
        disabled={saveMutation.isPending}
        className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 text-sm disabled:opacity-50"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={() => {
          setError(null);
          saveMutation.mutate();
        }}
        disabled={saveMutation.isPending || loadingEmployees}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium disabled:opacity-50"
      >
        {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {isEdit ? 'Guardar cambios' : 'Registrar ausencia'}
      </button>
    </>
  );

  return (
    <LaborCalendarModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar ausencia' : 'Nueva ausencia'}
      icon={CalendarOff}
      footer={footer}
      size="lg"
    >
      {error && (
        <div className="mb-4 p-3 rounded-lg border border-red-800 bg-red-900/20 flex gap-2 text-red-300 text-sm">
          <AlertCircle className="w-5 h-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label htmlFor="absence-employee" className="block text-sm text-gray-300 mb-2">
            Empleado
          </label>
          <select
            id="absence-employee"
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            disabled={isEdit || loadingEmployees}
            className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-60"
          >
            <option value="">
              {loadingEmployees ? 'Cargando empleados…' : 'Seleccionar empleado…'}
            </option>
            {selectableEmployees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {getEmployeeDisplayName(emp)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="absence-type" className="block text-sm text-gray-300 mb-2">
            Tipo de ausencia
          </label>
          <select
            id="absence-type"
            value={absenceType}
            onChange={(e) => setAbsenceType(e.target.value)}
            className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
          >
            {ABSENCE_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="absence-start" className="block text-sm text-gray-300 mb-2">
              Fecha inicio
            </label>
            <input
              id="absence-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label htmlFor="absence-end" className="block text-sm text-gray-300 mb-2">
              Fecha fin
            </label>
            <input
              id="absence-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div>
          <label htmlFor="absence-reason" className="block text-sm text-gray-300 mb-2">
            Motivo (opcional)
          </label>
          <textarea
            id="absence-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Detalle adicional sobre la ausencia…"
            className="w-full bg-gray-900 border border-gray-600 text-white rounded-lg px-3 py-2 text-sm resize-y min-h-[80px]"
          />
        </div>
      </div>
    </LaborCalendarModalShell>
  );
}
